import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import HolidayVillageOutlinedIcon from "@mui/icons-material/HolidayVillageOutlined";
import LandscapeOutlinedIcon from "@mui/icons-material/LandscapeOutlined";
import ListRoundedIcon from "@mui/icons-material/ListRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SailingOutlinedIcon from "@mui/icons-material/SailingOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import VillaOutlinedIcon from "@mui/icons-material/VillaOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTranslation } from "react-i18next";

import { ApiConstants } from "../../../main/constants/ApiConstants";
import {
  getPrimaryHotelCategory,
  getSelectedHotelCategories,
  hasHotelCategory,
  HOTEL_CATEGORY_OPTIONS,
  HotelCategoryFilter,
  HotelCategoryFlag,
} from "../../../main/constants/hotelCategories";
import IHotel from "../../../main/interfaces/IHotel";
import "../../../main/theme/theme.css";
import { AppTheme, getStoredMainLandingTheme, MAIN_LANDING_THEMES } from "../../../main/theme/publicTheme";
import { buildEntityFileUrl } from "../../../main/utils/fileUrl";
import IRoomType from "../../../main/interfaces/IRoomType";
import PublicCurrencySwitch from "./PublicCurrencySwitch";
import PublicLanguageSwitch from "./PublicLanguageSwitch";
import { formatPublicPrice } from "./publicBookingCurrency";
import "./OnlineReservation.css";
import "./PublicExperience.css";
import "./OnlineReservationHotels.css";

type PublicHotelModel = IHotel & {
  note?: string;
  coverImageUrl?: string;
  categories?: number;
};

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

type SortMode = "name" | "price" | "roomTypes";
type PriceFilter = "all" | "budget" | "mid" | "premium";
type ViewMode = "grid" | "list";

type HotelRoomSummary = {
  roomTypes: IRoomType[];
  minNightPrice: number | null;
  maxOccupancy: number | null;
};

const HOTEL_CATEGORIES: Array<{ id: HotelCategoryFilter; labelKey: string }> = [
  { id: "all", labelKey: "publicBooking.categoryAll" },
  ...HOTEL_CATEGORY_OPTIONS.map((category) => ({ id: category.id, labelKey: category.labelKey })),
];

const THEME_LABELS: Record<AppTheme, string> = {
  light: "Light",
  sky: "Sky",
  dark: "Dark",
  ocean: "Ocean",
  pearl: "Pearl",
  forest: "Forest",
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

const getHotelImageUrl = (hotel: PublicHotelModel) => buildEntityFileUrl(hotel.id, ApiConstants.EntityTypes.Hotel);

const normalizeHotel = (hotel: any): PublicHotelModel => ({
  ...hotel,
  id: Number(hotel?.id ?? hotel?.Id ?? 0),
  companyId: hotel?.companyId ?? hotel?.CompanyId,
  name: hotel?.name ?? hotel?.Name ?? "",
  code: hotel?.code ?? hotel?.Code ?? "",
  address: hotel?.address ?? hotel?.Address ?? "",
  note: hotel?.note ?? hotel?.Note ?? "",
  timezone: hotel?.timezone ?? hotel?.Timezone ?? "",
  checkInTime: hotel?.checkInTime ?? hotel?.CheckInTime ?? "",
  checkOutTime: hotel?.checkOutTime ?? hotel?.CheckOutTime ?? "",
  categories: Number(hotel?.categories ?? hotel?.Categories ?? 0),
  currencyId: hotel?.currencyId ?? hotel?.CurrencyId,
  created: hotel?.created ?? hotel?.Created,
  deleted: hotel?.deleted ?? hotel?.Deleted ?? null,
  coverImageUrl: hotel?.coverImageUrl ?? hotel?.CoverImageUrl ?? "",
});

const normalizeRoomType = (roomType: any): IRoomType => ({
  ...roomType,
  id: Number(roomType?.id ?? roomType?.Id ?? 0),
  hotelId: Number(roomType?.hotelId ?? roomType?.HotelId ?? 0),
  name: roomType?.name ?? roomType?.Name ?? "",
  code: roomType?.code ?? roomType?.Code ?? "",
  baseOccupancy: Number(roomType?.baseOccupancy ?? roomType?.BaseOccupancy ?? 0),
  maxOccupancy: Number(roomType?.maxOccupancy ?? roomType?.MaxOccupancy ?? 0),
  defaultNightPrice: Number(roomType?.defaultNightPrice ?? roomType?.DefaultNightPrice ?? 0),
  description: roomType?.description ?? roomType?.Description ?? "",
  created: roomType?.created ?? roomType?.Created,
  deleted: roomType?.deleted ?? roomType?.Deleted ?? null,
});

const buildRoomSummary = (roomTypes: IRoomType[]): HotelRoomSummary => {
  const prices = roomTypes
    .map((roomType) => Number(roomType.defaultNightPrice ?? 0))
    .filter((price) => price > 0);
  const occupancies = roomTypes
    .map((roomType) => Number(roomType.maxOccupancy ?? 0))
    .filter((occupancy) => occupancy > 0);

  return {
    roomTypes,
    minNightPrice: prices.length > 0 ? Math.min(...prices) : null,
    maxOccupancy: occupancies.length > 0 ? Math.max(...occupancies) : null,
  };
};

const hotelNameCollator = new Intl.Collator("sq-AL", {
  sensitivity: "base",
  numeric: true,
});

const getCategoryIcon = (category: HotelCategoryFlag) => {
  switch (category) {
    case HotelCategoryFlag.Luxury:
      return <WorkspacePremiumOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.Coast:
      return <SailingOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.Mountain:
      return <LandscapeOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.Boutique:
      return <VillaOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.Business:
      return <BusinessCenterOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.Family:
      return <HolidayVillageOutlinedIcon fontSize="inherit" />;
    case HotelCategoryFlag.City:
      return <ApartmentOutlinedIcon fontSize="inherit" />;
    default:
      return <MapsHomeWorkOutlinedIcon fontSize="inherit" />;
  }
};

const OnlineReservationHotels = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useState<AppTheme>(getStoredMainLandingTheme);
  const [hotels, setHotels] = useState<PublicHotelModel[]>([]);
  const [roomTypeMap, setRoomTypeMap] = useState<Record<number, HotelRoomSummary>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<HotelCategoryFilter>("all");
  const [sortBy] = useState<SortMode>("name");
  const [priceFilter] = useState<PriceFilter>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedCurrency, setSelectedCurrency] = useState("ALL");
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();

  useEffect(() => {
    if (!isThemeOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!themeDropdownRef.current?.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
      if (!mobileMenuRef.current?.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsThemeOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isThemeOpen]);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        setLoading(true);
        const response = await publicApi.get(ApiConstants.publicHotel.list);
        const items = Array.isArray(response.data) ? response.data.map(normalizeHotel) : [];
        setHotels(items.filter((hotel) => !hotel.deleted));
      } catch (error: any) {
        toast.error(error?.response?.data || t("publicBooking.loadHotelsFailed"));
      } finally {
        setLoading(false);
      }
    };

    void loadHotels();
  }, [t]);

  useEffect(() => {
    const loadRoomSummaries = async () => {
      if (hotels.length === 0) {
        setRoomTypeMap({});
        return;
      }

      const results = await Promise.all(
        hotels.map(async (hotel) => {
          try {
            const response = await publicApi.get(ApiConstants.publicHotel.roomTypes, {
              params: { hotelId: hotel.id },
            });

            const payload = response.data?.roomTypes ?? response.data?.items ?? response.data ?? [];
            const roomTypes = Array.isArray(payload) ? payload.map(normalizeRoomType) : [];
            return [hotel.id, buildRoomSummary(roomTypes)] as const;
          } catch {
            return [hotel.id, buildRoomSummary([])] as const;
          }
        })
      );

      setRoomTypeMap(Object.fromEntries(results));
    };

    void loadRoomSummaries();
  }, [hotels]);

  const filteredHotels = useMemo(() => {
    const term = search.trim().toLowerCase();

    let items = hotels.filter((hotel) => {
      const matchesSearch =
        !term ||
        [hotel.name, hotel.code, hotel.address, hotel.timezone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesCategory = selectedCategory === "all" || hasHotelCategory(hotel.categories, selectedCategory);
      const price = roomTypeMap[hotel.id]?.minNightPrice ?? null;
      const matchesPrice =
        priceFilter === "all" ||
        (priceFilter === "budget" && price !== null && price <= 50) ||
        (priceFilter === "mid" && price !== null && price > 50 && price <= 100) ||
        (priceFilter === "premium" && price !== null && price > 100);

      return matchesSearch && matchesCategory && matchesPrice;
    });

    items = [...items].sort((left, right) => {
      const leftSummary = roomTypeMap[left.id];
      const rightSummary = roomTypeMap[right.id];

      if (sortBy === "price") {
        const leftPrice = leftSummary?.minNightPrice;
        const rightPrice = rightSummary?.minNightPrice;

        if (leftPrice === null && rightPrice === null) return hotelNameCollator.compare(left.name, right.name);
        if (leftPrice === null) return 1;
        if (rightPrice === null) return -1;

        return leftPrice - rightPrice || hotelNameCollator.compare(left.name, right.name);
      }

      if (sortBy === "roomTypes") {
        const leftCount = leftSummary?.roomTypes.length ?? 0;
        const rightCount = rightSummary?.roomTypes.length ?? 0;

        return rightCount - leftCount || hotelNameCollator.compare(left.name, right.name);
      }

      return hotelNameCollator.compare(left.name, right.name);
    });

    return items;
  }, [hotels, priceFilter, roomTypeMap, search, selectedCategory, sortBy]);

  const uniqueCities = useMemo(
    () =>
      new Set(
        hotels
          .map((hotel) => hotel.address?.split(",")[0]?.trim())
          .filter((value): value is string => Boolean(value))
      ).size,
    [hotels]
  );

  const totalRoomTypes = useMemo(
    () => Object.values(roomTypeMap).reduce((sum, item) => sum + item.roomTypes.length, 0),
    [roomTypeMap]
  );

  return (
    <div className={`or-hotels-page bcShell bcTheme-${theme}`}>
      <header className="or-hotels-top-nav">
        <div className="or-hotels-nav-container">
          <div className="or-hotels-nav-left">
            <button type="button" className="or-hotels-monitor-btn" onClick={() => navigate("/")}>
              <KeyboardBackspaceOutlinedIcon fontSize="inherit" />
              {t("generic.back")}
            </button>
            <button type="button" className="or-hotels-brand" onClick={() => navigate("/")}>
              <span className="or-hotels-brand-icon">
                <ApartmentOutlinedIcon fontSize="inherit" />
              </span>
              <span className="or-hotels-brand-text">
                {t("publicBooking.heroTitleLine1", { defaultValue: "Online Reservation" })}
              </span>
            </button>
          </div>

          <div className={`or-hotels-nav-right ${isMobileMenuOpen ? "is-open" : ""}`} ref={mobileMenuRef}>
            <button
              type="button"
              className="or-hotels-mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
              aria-haspopup="menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={t("generic.menu", { defaultValue: "Menu" })}
            >
              <MenuRoundedIcon fontSize="inherit" />
            </button>

            <div className={`or-hotels-nav-actions ${isMobileMenuOpen ? "is-open" : ""}`} role="menu">
              <button
                type="button"
                className="or-hotels-monitor-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate("/home");
                }}
              >
                <LoginOutlinedIcon fontSize="inherit" />
                {t("publicBooking.openMonitor")}
              </button>

              <div className="theme-dropdown or-hotels-theme-dropdown" ref={themeDropdownRef}>
                <button
                  type="button"
                  className="theme-dropdown-trigger or-hotels-theme-trigger"
                  onClick={() => setIsThemeOpen((current) => !current)}
                  aria-haspopup="menu"
                  aria-expanded={isThemeOpen}
                  aria-label={t("generic.theme", { defaultValue: "Theme" })}
                >
                  <PaletteOutlinedIcon fontSize="inherit" />
                  <span>{THEME_LABELS[theme]}</span>
                  <KeyboardArrowDownRoundedIcon fontSize="inherit" />
                </button>

                {isThemeOpen ? (
                  <div className="theme-dropdown-menu or-hotels-theme-menu" role="menu">
                    {MAIN_LANDING_THEMES.map((themeOption) => (
                      <button
                        key={themeOption}
                        type="button"
                        className={`theme-dropdown-item ${theme === themeOption ? "active" : ""}`}
                        onClick={() => {
                          setTheme(themeOption);
                          setIsThemeOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        role="menuitemradio"
                        aria-checked={theme === themeOption}
                      >
                        <span className={`theme-dot theme-dot--${themeOption}`} />
                        <span>{THEME_LABELS[themeOption]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <PublicCurrencySwitch value={selectedCurrency} onChange={setSelectedCurrency} />

              <PublicLanguageSwitch />
            </div>
          </div>
        </div>
      </header>

      <section className="or-hotels-hero">
        <div className="or-hotels-shell or-hotels-hero-layout">
          <div className="or-hotels-hero-copy">
            <div className="or-hotels-hero-topline">
              <span className="or-hotels-hero-badge">{t("publicBooking.liveAvailability", { defaultValue: "Live availability" })}</span>
            </div>

            <h1 className="or-hotels-hero-title">
              {t("publicBooking.heroTitleLine1")}
              <br />
              <span>{t("publicBooking.heroTitleLine2")}</span>
            </h1>

            <p className="or-hotels-hero-text">{t("publicBooking.heroText")}</p>
          </div>

          <div className="or-hotels-hero-stats">
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">
                <ApartmentOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{hotels.length}+</strong>
              <span>{t("publicBooking.partnerHotels")}</span>
            </div>
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">
                <LocationOnOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{uniqueCities}+</strong>
              <span>{t("publicBooking.cities")}</span>
            </div>
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">
                <BedOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{totalRoomTypes}+</strong>
              <span>{t("publicBooking.roomTypes")}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="or-hotels-shell or-hotels-filters">
        <div className="search-container or-hotels-search">
          <span className="or-hotels-search-icon" aria-hidden="true">
            <TravelExploreOutlinedIcon fontSize="inherit" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("publicBooking.searchPlaceholder")}
            className="search-input or-hotels-search-input"
          />
        </div>

        <div className="or-hotels-filter-row">
          <div className="or-hotels-filter-block">
            <label>{t("publicBooking.category")}</label>
            <div className="or-hotels-categories-row">
              <div className="or-hotels-categories">
                {HOTEL_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`or-hotels-chip ${selectedCategory === category.id ? "active" : ""}`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <span className="or-hotels-chip-icon" aria-hidden="true">
                      {getCategoryIcon(category.id === "all" ? HotelCategoryFlag.None : category.id)}
                    </span>
                    <span>{t(category.labelKey)}</span>
                  </button>
                ))}
              </div>

              <div className="or-hotels-view-block">
                <div className="or-hotels-view-switch">
                  <button
                    type="button"
                    className={viewMode === "grid" ? "active" : ""}
                    onClick={() => setViewMode("grid")}
                    aria-label={t("publicBooking.gridView")}
                  >
                    <GridViewRoundedIcon fontSize="inherit" />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "list" ? "active" : ""}
                    onClick={() => setViewMode("list")}
                    aria-label={t("publicBooking.listView")}
                  >
                    <ListRoundedIcon fontSize="inherit" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="or-hotels-results-line">
          <strong>{filteredHotels.length}</strong> {t("publicBooking.hotelsFound")}
        </div>
      </section>

      {loading ? (
        <div className="or-hotels-shell or-hotels-empty">
          <strong>{t("publicBooking.loadingHotels")}</strong>
        </div>
      ) : filteredHotels.length === 0 ? (
        <div className="or-hotels-shell or-hotels-empty">
          <strong>{t("publicBooking.noHotelsTitle")}</strong>
          <span>{t("publicBooking.noHotelsText")}</span>
        </div>
      ) : (
        <div className={`or-hotels-shell or-hotels-grid ${viewMode === "list" ? "or-hotels-grid--list" : ""}`}>
          {filteredHotels.map((hotel) => {
            const roomSummary = roomTypeMap[hotel.id] ?? buildRoomSummary([]);
            const roomTypeNames = roomSummary.roomTypes
              .map((roomType) => roomType.name)
              .filter(Boolean)
              .slice(0, 3);
            const hotelCategories = getSelectedHotelCategories(hotel.categories);
            const hotelCategory = getPrimaryHotelCategory(hotel.categories);
            const hotelCategoryLabelKey =
              HOTEL_CATEGORY_OPTIONS.find((item) => item.id === hotelCategory)?.labelKey || "publicBooking.categoryCity";

            return (
              <article
                key={hotel.id}
                className="or-hotels-card or-hotels-card--clickable"
                onClick={() => navigate(`/reservations/${toSlug(hotel.name)}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/reservations/${toSlug(hotel.name)}`);
                  }
                }}
              >
                <div className="or-hotels-card-top">
                  <div
                    className="or-hotels-card-image"
                    style={{ backgroundImage: `url(${getHotelImageUrl(hotel)})` }}
                  >
                    <div className="or-hotels-card-overlay">
                      <span className="or-hotels-online-badge">
                        {getCategoryIcon(hotelCategory)}
                        {t(hotelCategoryLabelKey)}
                      </span>
                      <div className="or-hotels-card-overlay-copy">
                        <span className="or-hotels-card-overline">Hotel</span>
                        <h3>{hotel.name}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="or-hotels-card-bottom">
                  <div className="or-hotels-location">
                    <LocationOnOutlinedIcon fontSize="inherit" />
                    {hotel.address || t("publicBooking.locationMissing")}
                  </div>
                  {hotel.note?.trim() ? <p className="or-hotels-description">{hotel.note}</p> : null}

                  <div className="or-hotels-price">
                    <strong>
                      {roomSummary.minNightPrice !== null
                        ? formatPublicPrice(roomSummary.minNightPrice, selectedCurrency, currentLanguage)
                        : t("publicBooking.onRequest")}
                    </strong>
                    {roomSummary.minNightPrice !== null && <span>{t("publicBooking.perNight")}</span>}
                  </div>

                  <div className="or-hotels-room-tags">
                    {hotelCategories.map((category) => (
                      <span key={category.id} className="or-hotels-tag">
                        {t(category.labelKey)}
                      </span>
                    ))}
                    {roomTypeNames.map((roomTypeName) => (
                      <span key={roomTypeName} className="or-hotels-tag">
                        {roomTypeName}
                      </span>
                    ))}
                    {roomSummary.maxOccupancy ? (
                      <span className="or-hotels-tag">{t("publicBooking.maxGuests", { count: roomSummary.maxOccupancy })}</span>
                    ) : null}
                  </div>

                  <div className="or-hotels-times">
                    <div>
                      <label>{t("reservationDesk.checkIn")}</label>
                      <span>{hotel.checkInTime || "14:00"}</span>
                    </div>
                    <div>
                      <label>{t("reservationDesk.checkOut")}</label>
                      <span>{hotel.checkOutTime || "11:00"}</span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OnlineReservationHotels;
